import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { Button, Typography } from '@mui/material';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import SimpleDialog from '@src/components/Dialog/SimpleDialog';
import DeleteIcon from '@src/assets/icons/delete.svg';
import apis from '@src/apis';
import actions from '@src/redux/actions';
import { downloadAudioLinks } from '@src/apis/requests';
import { trackAudioDownloaded, deriveModule } from '@src/services/moengage';
import { MOE_SURFACE } from '@src/constants/moengage';
import { REQUEST_STATUS } from '@src/constants/voice';
import { DOWNLOAD_QUOTA_EXCEEDED_CODE } from '@src/constants/tts';
import { StyledActionWarning, StyledRequestsActionHeader } from './index.style';

const RequestsActionHeader = ({
  selectedRequests,
  handleSelectAll,
  onDeleteSelectedRequests,
  isShowBtnSelectAll,
  isShowBtnDeselectAll,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [notification, setNotification] = useState({});

  const renderConfirmActions = ({ handleClose, handleConfirm }) => (
    <StyledActionWarning>
      <Button
        variant="outlined"
        onClick={handleClose}
        className="no-button"
        data-id="cancel-delete-req"
      >
        {t('noValue')}
      </Button>
      <Button
        variant="contained"
        onClick={handleConfirm}
        data-id="confirm-delete-req"
      >
        {t('yesValue')}
      </Button>
    </StyledActionWarning>
  );

  const getDownloadableRequests = (requests) => {
    const downloadableRequests = requests.filter(
      ({ status, retentionPeriod, createdAt }) => {
        const audioSuccess = status === REQUEST_STATUS.SUCCESS;
        const audioExpired = moment().isAfter(
          moment(createdAt).add(retentionPeriod, 'days'),
        );
        return audioSuccess && !audioExpired;
      },
    );

    return downloadableRequests;
  };

  const getExpiredRequests = (requests) => {
    const expiredRequests = requests.filter(
      ({ status, retentionPeriod, createdAt }) =>
        status === REQUEST_STATUS.SUCCESS &&
        moment().isAfter(moment(createdAt).add(retentionPeriod, 'days')),
    );
    return expiredRequests;
  };

  const getFailedRequests = (requests) => {
    const failedRequests = requests.filter(
      ({ status }) => status !== REQUEST_STATUS.SUCCESS,
    );
    return failedRequests;
  };

  const handleCloseNotification = () => setNotification({});

  const handleDeleteSelectedRequests = () => onDeleteSelectedRequests();

  const handleOpenDeleteRequests = () => {
    setNotification({
      open: true,
      title: t('deleteRequestTitle'),
      description: t('deleteRequestTitleDescription', {
        total: selectedRequests.length,
      }),
      actionComponent: renderConfirmActions({
        handleClose: handleCloseNotification,
        handleConfirm: () => {
          handleDeleteSelectedRequests();
          handleCloseNotification();
        },
      }),
    });
  };

  const handleDownloadRequests = async () => {
    const validRequests = getDownloadableRequests(selectedRequests);

    const presignedUrls = [];
    await Promise.all(
      validRequests.map(async ({ id, title }) => {
        try {
          const res = await apis.requests.getPresignedAudioUrl(id);
          if (res?.status === 1) presignedUrls.push(res.result?.audio);
          else {
            if (res?.errorCode === DOWNLOAD_QUOTA_EXCEEDED_CODE) {
              dispatch(actions.user.updateShowBlockDownloadDialog(true));
              return;
            }
            throw new Error();
          }
        } catch (error) {
          dispatch(
            actions.noti.push({
              severity: 'error',
              message: 'downloadError',
              value: title,
            }),
          );
        }
      }),
    );

    if (presignedUrls.length) {
      trackAudioDownloaded({
        module: deriveModule(validRequests[0]),
        surface: MOE_SURFACE.BULK,
        count: presignedUrls.length,
      });
    }
    downloadAudioLinks(presignedUrls);
  };

  const validateRequestsForDownload = (requests) => {
    const validAudios = getDownloadableRequests(requests);
    const expiredAudios = getExpiredRequests(requests);
    const failedAudios = getFailedRequests(requests);

    return { validAudios, expiredAudios, failedAudios };
  };

  const handleOpenDownloadPopup = () => {
    let open = true;
    let description = '';
    let actionComponent;

    const { validAudios, expiredAudios, failedAudios } =
      validateRequestsForDownload(selectedRequests);

    const totalValid = validAudios?.length || 0;
    const totalExpired = expiredAudios?.length || 0;
    const totalFailed = failedAudios?.length || 0;

    if (!totalValid) {
      // There is no valid audios
      const expireTime = expiredAudios[0]?.retentionPeriod;
      if (totalExpired > 0) description = t('invalidAudios', { expireTime });
      else description = t('cannotDownloadFailedRequests');
      actionComponent = (
        <StyledActionWarning>
          <Button onClick={handleCloseNotification} variant="contained">
            {t('understood')}
          </Button>
        </StyledActionWarning>
      );
    } else if (totalValid !== selectedRequests.length) {
      // There are some valid audios, some invalid
      if (totalFailed === 0) {
        description = t('downloadDescriptionForExpired', {
          total: selectedRequests.length,
          totalValid,
          totalInvalid: selectedRequests.length - totalValid,
          expire: expiredAudios[0]?.retentionPeriod,
        });
      } else if (totalExpired === 0) {
        description = t('downloadDescriptionForFailed', {
          total: selectedRequests.length,
          totalValid,
          totalInvalid: selectedRequests.length - totalValid,
        });
      } else {
        description = t('downloadDescription', {
          total: selectedRequests.length,
          totalValid,
          totalExpired,
          totalFailed,
          expire: expiredAudios[0]?.retentionPeriod,
        });
      }
      actionComponent = renderConfirmActions({
        handleClose: handleCloseNotification,
        handleConfirm: () => {
          handleDownloadRequests();
          handleCloseNotification();
        },
      });
    } else open = false;

    if (open)
      setNotification({
        open,
        title: t('downloadTitle'),
        description,
        actionComponent,
      });
    else handleDownloadRequests();
  };
  return (
    <StyledRequestsActionHeader className="action-header">
      <div className="selected">
        <Typography className="selected-requests ">
          {t('selectedRequests', { value: selectedRequests?.length || 0 })}
        </Typography>
        {isShowBtnSelectAll && (
          <Button
            variant="outlined"
            color="primary"
            type="checkox"
            onClick={(event) => handleSelectAll(event, 'checked')}
            className="detect-all-button"
            data-id="select-all-req"
          >
            {t('selectAll')}
          </Button>
        )}
        {isShowBtnDeselectAll && (
          <Button
            variant="outlined"
            color="primary"
            onClick={handleSelectAll}
            className="detect-all-button"
            data-id="unselect-all-req"
          >
            {t('deselectAll')}
          </Button>
        )}
        <Button
          variant="outlined"
          color="primary"
          onClick={handleOpenDownloadPopup}
          disabled={selectedRequests?.length === 0}
          className="download-button"
          startIcon={<DownloadRoundedIcon />}
          data-id="download-all-req"
        >
          {t('download')}
        </Button>
      </div>
      <Button
        variant="outlined"
        color="error"
        onClick={handleOpenDeleteRequests}
        disabled={selectedRequests?.length === 0}
        className="delete-button"
        startIcon={<img src={DeleteIcon} alt="delete" />}
        data-id="delete-all-req"
      >
        {t('delete')}
      </Button>

      <SimpleDialog
        name="notification-dialog"
        className="notification-dialog"
        title={notification?.title}
        description={notification?.description || ''}
        open={notification?.open}
        onClose={handleCloseNotification}
        actionComponents={notification?.actionComponent}
        PaperProps={{ sx: { borderRadius: '12px', padding: '20px' } }}
      />
    </StyledRequestsActionHeader>
  );
};

export default RequestsActionHeader;
